import {
  Content,
  Item,
  Portal,
  Root,
  Trigger,
} from '@radix-ui/react-dropdown-menu'
import { IconCheck } from '@tabler/icons-react'
import type { VariantProps } from 'class-variance-authority'
import {
  Children,
  type ComponentPropsWithoutRef,
  createContext,
  forwardRef,
  isValidElement,
  type ReactElement,
  type ReactNode,
  useContext,
} from 'react'
import { cn } from '#src/cn.ts'
import { Tabs } from '#src/components/Tabs/Tabs.tsx'
import type { TabsListProps } from '#src/components/Tabs/TabsList.tsx'
import {
  TabsList,
  type tabsListVariants,
} from '#src/components/Tabs/TabsList.tsx'
import { useOverflowTabs } from '#src/components/Tabs/useOverflowTabs.ts'

interface TabTriggerProps {
  value: string
  children?: ReactNode
  className?: string
}

interface ResponsiveTabsContextValue {
  value?: string
  onValueChange?: (value: string) => void
}

const ResponsiveTabsContext = createContext<ResponsiveTabsContextValue>({})

export function ResponsiveTabs({
  value,
  onValueChange,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof Tabs>): ReactNode {
  return (
    <ResponsiveTabsContext.Provider value={{ value, onValueChange }}>
      <Tabs value={value} onValueChange={onValueChange} {...props}>
        {children}
      </Tabs>
    </ResponsiveTabsContext.Provider>
  )
}

interface ResponsiveTabsListProps
  extends TabsListProps, VariantProps<typeof tabsListVariants> {
  moreLabel?: (count: number) => ReactNode
  minVisible?: number
}

export const ResponsiveTabsList = forwardRef<
  HTMLDivElement,
  ResponsiveTabsListProps
>(
  (
    {
      children,
      className,
      variant,
      moreLabel = (count) => `${count} more...`,
      minVisible = 1,
      ...props
    },
    ref
  ) => {
    const childArray = Children.toArray(children).filter(
      isValidElement
    ) as ReactElement<TabTriggerProps>[]

    const { containerRef, measurerRef, state } = useOverflowTabs({
      children,
      minVisible,
      moreButtonWidth: 80,
    })

    const visibleChildren = childArray.slice(0, state.visibleCount)
    const overflowChildren = childArray.slice(state.visibleCount)

    return (
      <div ref={containerRef} className="w-full overflow-hidden">
        <div
          ref={measurerRef}
          className={cn(
            'pointer-events-none invisible absolute flex',
            variant === 'default' ? 'gap-1' : 'gap-0'
          )}
          aria-hidden="true"
        >
          {childArray.map((child) => (
            <span
              key={`measure-${child.props.value}`}
              className={cn(
                'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm',
                child.props.className
              )}
            >
              {child.props.children}
            </span>
          ))}
        </div>
        <TabsList
          ref={ref}
          variant={variant}
          className={cn('w-fit', className)}
          {...props}
        >
          {visibleChildren}
          {state.isOverflowing && (
            <OverflowMenu
              overflowChildren={overflowChildren}
              moreLabel={moreLabel}
              variant={variant}
            />
          )}
        </TabsList>
      </div>
    )
  }
)
ResponsiveTabsList.displayName = 'ResponsiveTabsList'

interface OverflowMenuProps {
  overflowChildren: ReactElement<TabTriggerProps>[]
  moreLabel: (count: number) => ReactNode
  variant?: VariantProps<typeof tabsListVariants>['variant']
}

function OverflowMenu({
  overflowChildren,
  moreLabel,
  variant,
}: OverflowMenuProps): ReactNode {
  const { value: currentValue, onValueChange } = useContext(
    ResponsiveTabsContext
  )

  const hasActiveOverflow = overflowChildren.some(
    (child) => child.props.value === currentValue
  )

  return (
    <Root>
      <Trigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex items-center justify-center whitespace-nowrap rounded-md px-3 py-1.5 text-sm ring-offset-white transition-all focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:ring-offset-zinc-950 dark:focus-visible:ring-zinc-300',
            hasActiveOverflow &&
              variant !== 'underline' &&
              'bg-card text-card-foreground shadow-sm',
            hasActiveOverflow &&
              variant === 'underline' &&
              'border-teal-600 border-b-2 text-teal-600'
          )}
        >
          {moreLabel(overflowChildren.length)}
        </button>
      </Trigger>
      <Portal>
        <Content
          sideOffset={4}
          className="z-50 min-w-32 overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95"
        >
          {overflowChildren.map((child) => (
            <Item
              key={child.props.value}
              className={cn(
                'relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden transition-colors focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                hasActiveOverflow && 'pl-8'
              )}
              onSelect={() => onValueChange?.(child.props.value)}
            >
              {child.props.value === currentValue && (
                <span className="absolute left-2 flex size-4 items-center justify-center">
                  <IconCheck className="size-4" />
                </span>
              )}
              <span className="flex w-full items-center justify-between gap-2">
                {child.props.children}
              </span>
            </Item>
          ))}
        </Content>
      </Portal>
    </Root>
  )
}
